// src/messaging/NewConversationPanel.jsx
// 学生の「新しい会話」【2026-10-16】
//   (a) 先生に連絡: 授業を選ぶ → 先生の受付状況を書く前に見せる → 送れるなら会話を開く
//   (b) 授業グループ: 授業ごとの履修者だけの場を開く
//   (c) グループを作る
//   (d) 学籍番号で誘う（いてもいなくても同じ返事）
//   (e) QR・近づけて追加はアプリだけ
import React, { useEffect, useState } from 'react';
import { msgApi } from './api';
import { dateHour, dateOnly } from './util';
import { Modal } from './ui';

/** 履修している授業（学生ダッシュボードと同じ API）。need が false なら取りに行かない */
function useMyClasses(need) {
  const [classes, setClasses] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!need) { setClasses([]); return undefined; }
    let alive = true;
    let token = null;
    try { token = localStorage.getItem('token'); } catch (_) { /* noop */ }
    fetch('/api/dashboard/student', { headers: token ? { Authorization: 'Bearer ' + token } : {} })
      .then(r => r.json())
      .then(d => {
        if (!alive) return;
        if (d && d.success) setClasses(((d.dashboard && d.dashboard.classes) || []).map(c => ({ id: c.id, name: c.name })));
        else { setClasses([]); setErr((d && d.message) || '授業を読み込めませんでした'); }
      })
      .catch(() => { if (alive) { setClasses([]); setErr('授業を読み込めませんでした'); } });
    return () => { alive = false; };
  }, [need]);
  return { classes, err };
}

function stateText(a) {
  if (!a) return '';
  if (a.state === 'open') return 'いまメッセージを受け付けています。';
  if (a.state === 'off_hours') return `いまは受付時間外です。送ったメッセージは${a.next_open_at ? `${dateHour(a.next_open_at)}に` : '次の受付時間に'}先生へ通知されます。`;
  if (a.state === 'paused') return `先生は${a.pause && a.pause.ends_on ? `${dateOnly(a.pause.ends_on)}まで` : 'しばらく'}受付を休んでいます。送ったメッセージは受付が始まってから通知されます。${a.pause && a.pause.note ? `（${a.pause.note}）` : ''}`;
  if (a.state === 'closed') return 'この先生はメッセージを受け付けていません。';
  return '';
}

function TeacherContact({ classes, onOpen }) {
  const [sel, setSel] = useState(null);     // 授業
  const [info, setInfo] = useState(null);   // contact の応答
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const pick = async (c) => {
    setSel(c); setInfo(null); setErr('');
    const r = await msgApi.get(`/classes/${c.id}/contact`);
    if (!r.success) { setErr(r.message || '先生の受付状況を確かめられませんでした'); return; }
    setInfo(r);
  };

  const start = async () => {
    setBusy(true); setErr('');
    const r = await msgApi.post(`/classes/${sel.id}/teacher-dm`);
    setBusy(false);
    if (r.success) { onOpen(r.conversation_id); return; }
    if (r.code === 'TEACHER_CLOSED') {
      setInfo(i => ({ ...i, acceptance: { ...(i && i.acceptance), state: 'closed', can_send: false, fallback_text: r.fallback_text || (i && i.acceptance && i.acceptance.fallback_text) } }));
      return;
    }
    setErr(r.message || '開けませんでした');
  };

  if (!sel) {
    return (
      <>
        <p className="msg-help">どの授業の先生に連絡しますか？</p>
        <ClassButtons classes={classes} onPick={pick} />
      </>
    );
  }
  const a = info && info.acceptance;
  return (
    <div>
      <button type="button" className="btn-link" onClick={() => { setSel(null); setInfo(null); setErr(''); }}>← 授業を選び直す</button>
      <h4 className="msg-panel-h">{sel.name}{info && info.teacher ? `（${info.teacher.name} 先生）` : ''}</h4>
      {!info && !err && <p className="msg-help">受付状況を確かめています...</p>}
      {err && <p className="msg-error">{err}</p>}
      {a && (
        <div className={'msg-banner ' + (a.state === 'open' ? 'ok' : a.state === 'closed' ? 'warn' : 'info')}>
          <p>{stateText(a)}
            {a.fallback_text && (a.state !== 'open') && <><br /><span className="msg-prewrap">{a.fallback_text}</span></>}
          </p>
        </div>
      )}
      {a && (
        <div className="msg-row-buttons left">
          {a.can_send && (
            <button type="button" className="btn-primary" onClick={start} disabled={busy}>
              {info.conversation_id ? 'メッセージを開く' : 'メッセージを書く'}
            </button>
          )}
          {!a.can_send && info.conversation_id && (
            <button type="button" className="btn-secondary" onClick={() => onOpen(info.conversation_id)}>これまでのやり取りを見る</button>
          )}
        </div>
      )}
    </div>
  );
}

function ClassButtons({ classes, onPick, busyId }) {
  if (classes === null) return <p className="msg-help">読み込み中...</p>;
  if (!classes.length) return <p className="msg-help">履修している授業がありません。</p>;
  return (
    <ul className="msg-pick-list">
      {classes.map(c => (
        <li key={c.id}><button type="button" onClick={() => onPick(c)} disabled={busyId === c.id}>{c.name}</button></li>
      ))}
    </ul>
  );
}

function ClassGroups({ classes, onOpen }) {
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');
  const pick = async (c) => {
    setBusyId(c.id); setErr('');
    const r = await msgApi.post(`/classes/${c.id}/group`);
    setBusyId(null);
    if (!r.success) { setErr(r.message || '開けませんでした'); return; }
    onOpen(r.conversation_id);
  };
  return (
    <>
      <p className="msg-help">授業ごとの、履修している学生だけの場です。先生は入っていません。</p>
      {err && <p className="msg-error">{err}</p>}
      <ClassButtons classes={classes} onPick={pick} busyId={busyId} />
    </>
  );
}

function CreateGroup({ onOpen }) {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const create = async (e) => {
    e && e.preventDefault();
    if (!title.trim()) return;
    setBusy(true); setErr('');
    const r = await msgApi.post('/groups', { title: title.trim() });
    setBusy(false);
    if (!r.success) { setErr(r.message || '作れませんでした'); return; }
    onOpen(r.conversation.id);
  };
  return (
    <form onSubmit={create}>
      <div className="form-row">
        <label htmlFor="msg-new-group">グループ名</label>
        <input id="msg-new-group" type="text" maxLength={100} value={title} onChange={e => setTitle(e.target.value)} placeholder="例：〇〇ゼミ 3班" />
      </div>
      <p className="msg-help">作ったあと、メンバーの画面から学籍番号で誘えます。学期の終わりに続けるかを確認します。</p>
      {err && <p className="msg-error">{err}</p>}
      <div className="msg-row-buttons left">
        <button type="submit" className="btn-primary" disabled={busy || !title.trim()}>作る</button>
      </div>
    </form>
  );
}

function InviteById({ groups }) {
  const [sid, setSid] = useState('');
  const [note, setNote] = useState('');
  const [group, setGroup] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const send = async (e) => {
    e && e.preventDefault();
    if (!sid.trim()) return;
    setBusy(true); setMsg(''); setErr('');
    const r = await msgApi.post('/requests/student-id', {
      student_id: sid.trim(), conversation_id: group ? Number(group) : undefined, message: note.trim() || undefined,
    });
    setBusy(false);
    if (!r.success) { setErr(r.message || '送れませんでした'); return; }
    // 相手がいてもいなくても同じ返事にする（サーバーと同じ）
    setMsg('リクエストを送りました');
    setSid(''); setNote('');
  };
  return (
    <form onSubmit={send}>
      <div className="form-row">
        <label htmlFor="msg-invite-sid">相手の学籍番号</label>
        <input id="msg-invite-sid" type="text" value={sid} onChange={e => setSid(e.target.value)} autoComplete="off" />
      </div>
      {groups.length > 0 && (
        <div className="form-row">
          <label htmlFor="msg-invite-group">誘う先</label>
          <select id="msg-invite-group" value={group} onChange={e => setGroup(e.target.value)}>
            <option value="">1対1のメッセージ</option>
            {groups.map(g => <option key={g.id} value={g.id}>グループ「{g.title}」</option>)}
          </select>
        </div>
      )}
      <div className="form-row">
        <label htmlFor="msg-invite-note">ひとこと（なくてもかまいません）</label>
        <textarea id="msg-invite-note" rows={2} maxLength={200} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="例：〇〇の授業で同じ班の△△です" />
        <small className="msg-count">{note.length}/200</small>
      </div>
      <p className="msg-help">相手が承認すると始められます。</p>
      {err && <p className="msg-error">{err}</p>}
      {msg && <p className="msg-help strong">{msg}</p>}
      <div className="msg-row-buttons left">
        <button type="submit" className="btn-primary" disabled={busy || !sid.trim()}>リクエストを送る</button>
      </div>
    </form>
  );
}

export default function NewConversationPanel({ features, isDeveloper, conversations, onClose, onOpen }) {
  const on = (k) => !!isDeveloper || !!features[k];
  const tabs = [
    on('teacher_dm') && ['teacher', '先生に連絡'],
    on('class_chat') && ['class', '授業グループ'],
    on('student_chat') && ['group', 'グループを作る'],
    on('student_chat') && ['invite', '学籍番号で誘う'],
  ].filter(Boolean);
  const [tab, setTab] = useState(tabs.length ? tabs[0][0] : null);
  const needClasses = on('teacher_dm') || on('class_chat');
  const { classes, err } = useMyClasses(needClasses);
  const groups = (conversations || []).filter(c => c.kind === 'group' && c.status === 'active' && !c.left);
  const open = (id) => { onClose(); onOpen(id); };

  return (
    <Modal title="新しい会話" onClose={onClose} wide>
      {tabs.length === 0 ? <p className="msg-help">今は新しい会話を始められません。</p> : (
        <div className="tab-bar">
          {tabs.map(([k, l]) => (
            <button type="button" key={k} className={'tab-btn' + (tab === k ? ' active' : '')} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
      )}
      {err && (tab === 'teacher' || tab === 'class') && <p className="msg-error">{err}</p>}
      {tab === 'teacher' && <TeacherContact classes={classes} onOpen={open} />}
      {tab === 'class' && <ClassGroups classes={classes} onOpen={open} />}
      {tab === 'group' && <CreateGroup onOpen={open} />}
      {tab === 'invite' && <InviteById groups={groups} />}
      {on('student_chat') && <p className="msg-help msg-panel-foot">QR コード・近づけて追加はスマホのアプリで使えます</p>}
    </Modal>
  );
}
